import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Button } from '@material-ui/core';
import { ArrowDropDown } from '@material-ui/icons';
import _ from 'lodash';

import { makeURL } from '../../helperFunctions';
import {
  formatFilterValue,
  defaultFilterState,
  formatLabelValue,
  defaultSelectValues,
  mapIncomeGroupToBracket,
  buildFiltersFromFormData,
} from './helpers';
import useMarketSizePercentage from '../../myHooks/useMarketSizePercentage';

import FilterInputs from './filterComponents/FilterInputs';
import FilterBadge from './filterComponents/FilterBadge';
import CSVExportButton from '../../myComponents/CSVExportButton';
import '../../myComponents/customSwitch/index.css';

const themeColorConfig = {
  green: {
    filterSectionBackground: 'rgba(130,194,195, 0.2) !important',
    filterBadgeBackground: '#97CCCD',
    resetFilterButtonColor: '#19989B',
    filterIconUrl: '/images/parameter-settings-icon-green.png',
  },
  yellow: {
    filterSectionBackground: 'rgba(171,134,40, 0.2) !important',
    filterBadgeBackground: '#AB8628',
    resetFilterButtonColor: '#AB8628',
    filterIconUrl: '/images/parameter-settings-icon-yellow.png',
  },
  red: {
    filterSectionBackground: 'rgba(223,51,113, 0.2) !important',
    filterBadgeBackground: '#E45589',
    resetFilterButtonColor: '#E45589',
    filterIconUrl: '/images/parameter-settings-icon-red.png',
  },
};

const useStyles = makeStyles(theme => ({
  disabled: {
    position: 'absolute',
    top: '0',
    right: '0',
    left: '0',
    bottom: '0',
    zIndex: '100',
    backdropFilter: 'blur(1.8px)',
    backgroundColor: 'rgb(200 200 200 / 0.3)',
  },
  filterSection: props => ({
    width: '100%',
    backgroundColor: themeColorConfig[props.themeColor].filterSectionBackground,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  filterSectionContent: {
    '@media (max-width: 768px)': {
      justifyContent: 'space-between',
    },
  },
  filtersButton: {
    display: 'flex',
    padding: '10px 15px',
    borderRadius: '10px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filtersButtonImage: {
    width: '25px',
    marginRight: '15px',
  },
  filtersButtonText: {
    color: theme.palette.text.primary,
    fontSize: '15px',
    margin: 0,
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterSectionList: {
    height: '100%',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '20px',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  filterSectionListScroll: {
    height: '100%',
    padding: '6px 0px',
    maxWidth: '500px',
    display: 'flex',
    alignItems: 'center',
    alignItems: 'flex-start',
    gap: '12px',
    overflow: 'hidden',
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    paddingRight: '10px',
    ['-ms-overflow-style']: 'none',

    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  resetFiltersBtn: props => ({
    cursor: 'pointer',

    '& span': {
      color: themeColorConfig[props.themeColor].filterBadgeBackground,
      fontWeight: 700,
      fontStyle: 'italic',
    },
  }),
}));

const Filters = ({
  calculateHandler,
  themeColor,
  csvExport,
  disabled,
  pickedBatchCountry,
  pickedBatchData,
  onSelectedFiltersChange,
}) => {
  //Styles
  const classes = useStyles({ themeColor });

  //Chosen filters state
  const [selectedFilters, setSelectedFilters] = useState(defaultFilterState());

  //Filters show/hide
  const [openFilters, setOpenFilters] = useState(false);

  const [selectValues, setSelectValues] = useState(defaultSelectValues());
  const onChangeSelectValues = (type, value) => {
    setSelectValues({ ...selectValues, [type]: value });
  };

  const [resetAgeSliderValue, setResetAgeSliderValue] = useState(false);

  //Input States
  const [rangeWasTouched, setRangeWasTouched] = useState(false);

  const [draftFiltersToSend, setDraftFiltersToSend] = useState(defaultFilterState());
  const { formattedPercentage } = useMarketSizePercentage({
    filters: draftFiltersToSend,
    batches: pickedBatchData,
    batchCountry: pickedBatchCountry,
  });

  const onFiltersSubmit = event => {
    event.preventDefault();
    setOpenFilters(false);

    const data = new FormData(event.target);
    const { preparedFilterData, filterToSend } = buildFiltersFromFormData(data, rangeWasTouched);

    setSelectedFilters(preparedFilterData);
    setDraftFiltersToSend(filterToSend);
    if (typeof onSelectedFiltersChange === 'function') onSelectedFiltersChange(preparedFilterData);
    calculateHandler(filterToSend);
  };

  const onFilterRemoveHandler = (label, value) => {
    const mappedLabel = getLabelToRemove(label);
    const isAge = mappedLabel == 'Age';
    const mappedValue = isAge ? value.toString() : value;
    let selectedFiltersTemp = { ...selectedFilters };

    Object.keys(selectedFiltersTemp).forEach(key => {
      if (key == mappedLabel) {
        selectedFiltersTemp[key] = selectedFiltersTemp[key].filter(filterValue => {
          let mappedFilterValue = isAge ? `${filterValue}` : filterValue;
          return mappedFilterValue != mappedValue;
        });
      }
    });

    // Deactivate DOM input elements
    if (typeof selectValues[mappedLabel] !== 'undefined') {
      setSelectValues({ ...selectValues, [mappedLabel]: 0 });
    } else if (isAge) {
      const input = document.querySelector(`input[name="${mappedLabel}"][value="${JSON.stringify(value)}"]`);
      if (input) {
        // Radio
        input.checked = false;
      } else {
        // Slider
        setResetAgeSliderValue(true);
      }
    } else {
      const input = document.querySelector(`input[name="${mappedLabel}"][value="${mappedValue}"]`);
      if (input) input.checked = false;
    }

    const filtersToSend = { ...selectedFiltersTemp };
    const incomeGroupActivated = filtersToSend.Income.find(e => typeof e === 'string');
    if (incomeGroupActivated) filtersToSend.Income = mapIncomeGroupToBracket(filtersToSend.Income);

    setSelectedFilters(selectedFiltersTemp);
    setDraftFiltersToSend(filtersToSend);
    if (typeof onSelectedFiltersChange === 'function') onSelectedFiltersChange(selectedFiltersTemp);
    calculateHandler(filtersToSend);
  };

  const getLabelToRemove = label => {
    let mappedLabel = label;

    if (label === 'Ideology') {
      mappedLabel = 'PersonalIdeology_1';
    }

    return mappedLabel;
  };

  const onResetFiltersHandler = () => {
    const defaultState = defaultFilterState();
    const defaultSelectValuesState = defaultSelectValues();

    // Deactivate DOM input elements
    setSelectValues({ ...defaultSelectValues() });

    for (const [key, value] of Object.entries(selectedFilters)) {
      const mappedLabel = getLabelToRemove(key);
      if (Object.keys(defaultSelectValuesState).includes(mappedLabel)) continue; // Select dropdown already reset

      // Reset age sliders or checkboxes
      if (mappedLabel === 'Age') {
        if (!value.length) continue;
        const input = document.querySelector(
          `input[name="${mappedLabel}"][value="${JSON.stringify(selectedFilters[mappedLabel][0])}"]`,
        );
        if (input) input.checked = false;
        else setResetAgeSliderValue(true);
      }

      // Reset checkboxes
      if (!value.length) continue;
      value.forEach(filterValue => {
        const input = document.querySelector(`input[name="${mappedLabel}"][value="${filterValue}"]`);
        if (input) input.checked = false;
      });
    }

    setSelectedFilters({ ...defaultState });
    setDraftFiltersToSend({ ...defaultState });
    if (typeof onSelectedFiltersChange === 'function') onSelectedFiltersChange({ ...defaultState });
    calculateHandler({ ...defaultState });
  };

  const onFiltersChangeHandler = event => {
    const form = event.currentTarget;
    if (!form) return;

    const data = new FormData(form);
    const { filterToSend } = buildFiltersFromFormData(data, rangeWasTouched);
    setDraftFiltersToSend(filterToSend);
  };

  return (
    <Box position="relative">
      {disabled && <Box className={classes.disabled}></Box>}
      <Box className={classes.filterSection}>
        <Box
          className={classes.filterSectionContent}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          flex={1}>
          <Button onClick={() => setOpenFilters(prevState => !prevState)} className={classes.filtersButton}>
            <img
              alt="filters-button"
              src={makeURL(themeColorConfig[themeColor].filterIconUrl)}
              className={classes.filtersButtonImage}
            />
            <Typography component="div" variant="button" className={classes.filtersButtonText}>
              Consumer Settings <ArrowDropDown />
            </Typography>
          </Button>

          <Box className={classes.filterSectionList}>
            <Box className={classes.filterSectionListScroll}>
              {Object.entries(selectedFilters || {}).map(([key, values]) =>
                values.map((filterValueToMap, idx) => {
                  return (
                    <FilterBadge
                      key={idx}
                      label={formatLabelValue(key)}
                      valueToShow={formatFilterValue(key, filterValueToMap, pickedBatchCountry)}
                      value={filterValueToMap}
                      onRemove={onFilterRemoveHandler}
                      bgColor={themeColorConfig[themeColor].resetFilterButtonColor}
                    />
                  );
                }),
              )}
            </Box>
            {!_.isEqual(selectedFilters, defaultFilterState()) && (
              <Box className={classes.resetFiltersBtn} onClick={onResetFiltersHandler}>
                <span>Reset All</span>
              </Box>
            )}
          </Box>
        </Box>
        {csvExport && <CSVExportButton csvData={csvExport} containerStyle={{}} />}
      </Box>
      <FilterInputs
        onChangeSelectValues={onChangeSelectValues}
        selectValues={selectValues}
        resetAgeSliderValue={resetAgeSliderValue}
        setResetAgeSliderValue={setResetAgeSliderValue}
        themeColor={themeColor}
        show={openFilters}
        onFiltersSubmit={onFiltersSubmit}
        onFiltersChange={onFiltersChangeHandler}
        setRangeWasTouched={setRangeWasTouched}
        pickedBatchCountry={pickedBatchCountry}
        marketSizeFormattedPercentage={formattedPercentage}
      />
    </Box>
  );
};

export default Filters;
